"use client";

import { Elements } from "@stripe/react-stripe-js";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import getStripe from "@repo/stripe/context/utils";
import ExpressCheckoutButton from "@repo/stripe/express-checkout";
import { createSetup } from "@repo/stripe/actions/setup";
import { getStripeAttributionMetadata } from "@repo/statsig";
import { BRAND } from "@repo/tailwind-config/brand";
import { Routes } from "src/lib/routes";
import React, { useCallback, useEffect, useState } from "react";

const isOwlet = BRAND === "owlet";

interface PlanDetails {
  id: string;
  price: number;
  interval: "month" | "year";
  trialDays: number;
}

interface CheckoutExpressCheckoutProps {
  planDetails: PlanDetails;
  productName: string;
  userInfo: {
    email: string;
    name: string;
    metadata: Record<string, string>;
  };
  onAvailabilityChange?: (available: boolean) => void;
  /** Where Stripe redirects after a successful wallet payment. Defaults to
   *  /home; pass the originating path (e.g. /intake/{id}) when the express
   *  checkout is rendered outside the home flow so the user lands back on
   *  the page they came from. */
  successPath?: string;
}

const ELEMENTS_OPTIONS: StripeElementsOptions = {
  mode: "setup",
  currency: "usd",
  setup_future_usage: "off_session",
};

/**
 * Express Checkout (Apple Pay / Google Pay) for the embedded checkout page.
 * Self-contained with its own Elements provider — separate from the card
 * form's Elements to avoid payment_method_types conflicts.
 * Uses createSetup() with automatic_payment_methods; the webhook creates
 * the subscription when the setup intent succeeds.
 */
export function CheckoutExpressCheckout({
  onAvailabilityChange,
  successPath,
  ...props
}: CheckoutExpressCheckoutProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null);

  // getStripe() returns a cached singleton promise — don't use a cancelled flag,
  // as remounting would block the already-resolved promise from setting state.
  useEffect(() => {
    getStripe()
      .then((s) => {
        if (s) setStripe(s);
      })
      .catch(() => {});
  }, []);

  if (!stripe) return null;

  // Remount the inner element when the cadence (price/interval) changes.
  // Stripe's ExpressCheckoutElement does not propagate runtime updates to
  // `applePay.recurringPaymentRequest` after mount — the Apple Pay sheet
  // bakes in the amount and interval from the initial options. Without
  // this key, toggling Annual ↔ Monthly leaves the wallet sheet showing
  // the original cadence's price.
  const innerKey = `${props.planDetails.id}-${props.planDetails.interval}-${props.planDetails.price}`;

  return (
    <Elements stripe={stripe} options={ELEMENTS_OPTIONS}>
      <ExpressCheckoutInner
        key={innerKey}
        {...props}
        successPath={successPath}
        onAvailabilityChange={onAvailabilityChange}
      />
    </Elements>
  );
}

function ExpressCheckoutInner({
  planDetails,
  productName,
  userInfo,
  onAvailabilityChange,
  successPath,
}: CheckoutExpressCheckoutProps) {
  // TODO: expose isProcessing to parent to disable the card form's submit button while express checkout is in flight. Apple Pay's native scrim prevents double-submission on iOS but this should be handled for Google Pay.
  const [isProcessing, setIsProcessing] = useState(false);
  const [available, setAvailable] = useState(false);

  const handleAvailabilityChange = useCallback(
    (avail: boolean) => {
      setAvailable(avail);
      onAvailabilityChange?.(avail);
    },
    [onAvailabilityChange]
  );

  const handleCreateSetup = useCallback(
    async (info: {
      email: string;
      name: string;
      metadata: Record<string, string>;
    }) => {
      return createSetup({
        email: info.email || userInfo.email,
        name: info.name || userInfo.name,
        metadata: {
          ...userInfo.metadata,
          ...info.metadata,
          ...getStripeAttributionMetadata({
            brand: BRAND,
            trialDays: planDetails.trialDays,
          }),
        },
        planId: planDetails.id,
        planPrice: planDetails.price,
        planInterval: planDetails.interval,
        trialDays: planDetails.trialDays,
        productName,
      });
    },
    [planDetails, productName, userInfo]
  );

  return (
    <>
      <div className="mt-6">
        <ExpressCheckoutButton
          successPath={successPath ?? Routes.HOME}
          errorPath={Routes.ERROR}
          setIsProcessingPayment={setIsProcessing}
          planDetails={planDetails}
          productName={productName}
          source="checkout"
          onAvailabilityChange={handleAvailabilityChange}
          userInfo={userInfo}
          onCreateSetup={handleCreateSetup}
        />
      </div>
      {/* OR Divider — gray-300 on Kinsa's light bg, on-surface/40 on Owlet's dark bg */}
      {available && (
        <div className="flex items-center mt-6 mb-3">
          <div
            className={`flex-1 border-t ${isOwlet ? "border-on-surface/40" : "border-gray-300"}`}
          ></div>
          <span
            className={`px-4 text-sm ${isOwlet ? "text-foreground" : "text-gray-500"}`}
          >
            {isOwlet ? "Or pay by card" : "OR"}
          </span>
          <div
            className={`flex-1 border-t ${isOwlet ? "border-on-surface/40" : "border-gray-300"}`}
          ></div>
        </div>
      )}
    </>
  );
}
